import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import {
  copilotConfigKey,
  copilotPromptbooksKey,
  copilotAgentsKey,
  copilotSessionsKey,
} from './SecurityCopilotTab';

interface ConfigResponse {
  config: {
    customerId: string;
    enabled: boolean;
    mode: 'mock' | 'live';
    defaultPromptbookId: string | null;
    defaultAgentId: string | null;
  };
  serverLiveEnabled: boolean;
}
interface Item { id: string; name: string; description: string | null; }
interface ListResponse<T> {
  items: T[];
  mode: { configured: 'mock' | 'live'; effective: 'mock' | 'live'; reason: string | null };
}
interface RunResponse {
  sessionId: string;
  output: string;
  mode: 'mock' | 'live';
  scuCost: string | null;
  latencyMs: number;
}

/**
 * Small button shown on the incident drawer. Hidden when the customer doesn't
 * have Security Copilot enabled OR the user lacks Write on the security_copilot
 * tab. Opens an inline modal where analysts pick a promptbook / agent / freeform
 * prompt and run it against the incident. The result is streamed (sync for v1)
 * into the modal and persisted as a copilot_session linked to the incident.
 */
export function AskCopilotButton({
  customerId,
  incidentId,
  incidentTitle,
  incidentContext,
}: {
  customerId: string;
  incidentId: string;
  incidentTitle?: string;
  incidentContext?: Record<string, unknown>;
}) {
  const { canWrite, canCapability } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'promptbook' | 'agent' | 'prompt'>('promptbook');
  const [refId, setRefId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<string>(
    incidentTitle ? `Investigate incident "${incidentTitle}" and summarise the key findings, IOCs, and recommended next steps.` : '',
  );
  const [result, setResult] = useState<RunResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canRun = canWrite('security_copilot', customerId) && canCapability('security_copilot', 'run_copilot');

  const configQuery = useQuery({
    queryKey: copilotConfigKey(customerId),
    queryFn: () => apiFetch<ConfigResponse>(`/copilot/config/${encodeURIComponent(customerId)}`),
    enabled: !!customerId,
  });

  const promptbooksQuery = useQuery({
    queryKey: copilotPromptbooksKey(customerId),
    queryFn: () => apiFetch<ListResponse<Item>>(`/copilot/promptbooks/${encodeURIComponent(customerId)}`),
    enabled: open && !!configQuery.data?.config.enabled,
    retry: false,
  });
  const agentsQuery = useQuery({
    queryKey: copilotAgentsKey(customerId),
    queryFn: () => apiFetch<ListResponse<Item>>(`/copilot/agents/${encodeURIComponent(customerId)}`),
    enabled: open && !!configQuery.data?.config.enabled,
    retry: false,
  });

  const runMutation = useMutation({
    mutationFn: () =>
      apiFetch<RunResponse>('/copilot/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          incidentId,
          kind,
          refId: kind === 'prompt' ? null : refId,
          prompt,
          context: incidentContext,
        }),
      }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: copilotSessionsKey(customerId) });
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'Run failed');
      setResult(null);
    },
  });

  // Hide entirely if customer doesn't have Copilot enabled or user can't write.
  const cfg = configQuery.data?.config;
  if (!cfg?.enabled || !canRun) return null;

  return (
    <>
      <button
        onClick={() => {
          setOpen(true);
          setKind(cfg.defaultPromptbookId ? 'promptbook' : cfg.defaultAgentId ? 'agent' : 'prompt');
          setRefId(cfg.defaultPromptbookId ?? cfg.defaultAgentId ?? null);
          setResult(null);
          setError(null);
        }}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-purple-600 hover:bg-purple-700 text-white"
        title="Run a Microsoft Security Copilot promptbook or agent against this incident"
      >
        Ask Copilot
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Ask Security Copilot</h3>
                <p className="text-xs text-gray-500 dark:text-gray-100">
                  Incident <code className="font-mono">{incidentId}</code> -
                  mode: <span className={cfg.mode === 'live' ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}>{cfg.mode}</span>
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-xl leading-none">x</button>
            </div>

            <div className="flex gap-2">
              {(['promptbook', 'agent', 'prompt'] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => { setKind(k); setRefId(null); }}
                  className={`px-3 py-1 text-sm rounded border ${kind === k ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white'}`}
                >
                  {k === 'promptbook' ? 'Promptbook' : k === 'agent' ? 'Agent' : 'Free-form'}
                </button>
              ))}
            </div>

            {kind === 'promptbook' && (
              <select
                value={refId ?? ''}
                onChange={(e) => setRefId(e.target.value || null)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
              >
                <option value="">Select a promptbook...</option>
                {(promptbooksQuery.data?.items ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}
            {kind === 'agent' && (
              <select
                value={refId ?? ''}
                onChange={(e) => setRefId(e.target.value || null)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
              >
                <option value="">Select an agent...</option>
                {(agentsQuery.data?.items ?? []).map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            )}

            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-white">Prompt</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-100 mt-1">
                Incident IOCs and entities are auto-attached as structured context.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => runMutation.mutate()}
                disabled={runMutation.isPending || !prompt.trim() || (kind !== 'prompt' && !refId)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50 text-sm"
              >
                {runMutation.isPending ? 'Running...' : 'Run'}
              </button>
              {result && (
                <span className="text-xs text-gray-500 dark:text-gray-100">
                  {result.mode} - {result.latencyMs}ms
                  {result.scuCost ? ` - ${result.scuCost} SCU` : ''}
                </span>
              )}
            </div>

            {error && (
              <div className="rounded border border-red-300 bg-red-50 dark:bg-red-900/30 dark:border-red-700 p-3 text-sm text-red-700 dark:text-red-200">
                {error}
              </div>
            )}
            {result && (
              <div className="rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-3">
                <div className="text-xs font-semibold text-gray-700 dark:text-white mb-1">Response</div>
                <pre className="text-xs whitespace-pre-wrap text-gray-900 dark:text-gray-100">{result.output}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
